/**
 * Pure check for the "enter a URL" audio field. Rejects local file paths and
 * share-page links (web pages, not audio) with a clear message instead of
 * letting them reach an STT engine and come back as a server error.
 *
 * The same field carries our own Supabase storage signed URLs, so any other
 * http(s) host must stay ok.
 */
export type AudioUrlCheck =
  | { ok: true }
  | { ok: false; reason: 'empty' | 'not_a_web_link' | 'share_page'; message: string };

const NOT_A_WEB_LINK_MESSAGE =
  'That is not a web link. To transcribe a file from your computer, use the File tab. ' +
  'To use a link, paste a direct https:// link to the audio or video file.';

const SHARE_PAGE_MESSAGE =
  'That link opens a sharing page, not the audio file itself. ' +
  'Download the file to your computer and use the File tab instead.';

const SHARE_PAGE_HOSTS = [
  'drive.google.com',
  'docs.google.com',
  'onedrive.live.com',
  '1drv.ms',
  'sharepoint.com',
  'dropbox.com',
  'wetransfer.com',
  'we.tl',
  'icloud.com',
  'mega.nz',
  'mega.io',
  'box.com',
];

const hostIs = (host: string, h: string) => host === h || host.endsWith(`.${h}`);

/** Direct-download forms of otherwise blocked hosts. These return the file, not a sharing page. */
function isDirectDownload(host: string, url: URL): boolean {
  const q = url.searchParams;
  if (hostIs(host, 'dropbox.com')) return q.get('dl') === '1' || q.get('raw') === '1';
  if (host === 'drive.google.com' || host === 'docs.google.com') {
    return url.pathname === '/uc' && q.get('export') === 'download';
  }
  if (hostIs(host, 'sharepoint.com')) return q.get('download') === '1';
  return false;
}

/**
 * The link error belongs to the link that produced it: editing the link or switching the input tab
 * clears it. Pure so the rule is testable without rendering App.
 */
export function fileErrorAfterLinkOrTabChange(_current: string | null): string | null {
  return null;
}

export function checkAudioUrl(input: string): AudioUrlCheck {
  const trimmed = (input ?? '').trim();
  if (!trimmed) return { ok: false, reason: 'empty', message: 'Enter a link to the audio or video file.' };

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return { ok: false, reason: 'not_a_web_link', message: NOT_A_WEB_LINK_MESSAGE };
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    return { ok: false, reason: 'not_a_web_link', message: NOT_A_WEB_LINK_MESSAGE };
  }

  const host = url.hostname.toLowerCase().replace(/\.$/, '');
  const blocked = SHARE_PAGE_HOSTS.some((h) => hostIs(host, h));
  if (blocked && !isDirectDownload(host, url)) return { ok: false, reason: 'share_page', message: SHARE_PAGE_MESSAGE };

  return { ok: true };
}
